/**
 * TG-6 real client for TG-2's live contributor completion endpoints
 * (`api.functions.contributor_sections`): GET/PUT /api/portal/sections/{id}
 * and POST /api/portal/sections/submit. These track a delegated section's
 * COMPLETION STATE only -- the exposure answers themselves still go
 * through the shared `WorkingCopyPort` (this module's own doc comment:
 * "this feature owns no section-content or exposure-answer store").
 */
import { authenticatedFetch } from "./authenticated-fetch";

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

export type ContributorSectionState = "todo" | "in_progress" | "completed";

export interface ContributorSectionApiError {
  readonly code: string;
  readonly message: string;
  readonly incompleteSectionIds?: readonly string[];
}

export type ContributorSectionApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: ContributorSectionApiError };

async function parseError(response: Response): Promise<ContributorSectionApiError> {
  try {
    const body = (await response.json()) as Record<string, unknown>;
    return {
      code: typeof body.code === "string" ? body.code : "unknown",
      message: typeof body.message === "string" ? body.message : "Something went wrong. Try again.",
      ...(Array.isArray(body.incompleteSectionIds)
        ? { incompleteSectionIds: body.incompleteSectionIds as string[] }
        : {}),
    };
  } catch {
    return { code: "unknown", message: "Something went wrong. Try again." };
  }
}

/** GET .../sections/{sectionId}: the server's own check that a section
 * is in the contributor's delegated set (AC-19). A 403 `not_permitted`
 * is the caller's signal to show the not-permitted view. */
export async function getContributorSection(
  sectionId: string,
): Promise<ContributorSectionApiResult<{ sectionId: string; state: ContributorSectionState }>> {
  let response: Response;
  try {
    response = await authenticatedFetch(`/api/portal/sections/${encodeURIComponent(sectionId)}`, { method: "GET" });
  } catch {
    return { ok: false, error: { code: "unknown", message: "Something went wrong. Try again." } };
  }
  if (response.status === 200) {
    const body = (await response.json()) as { sectionId: string; state: ContributorSectionState };
    return { ok: true, value: { sectionId: body.sectionId, state: body.state } };
  }
  return { ok: false, error: await parseError(response) };
}

/** PUT .../sections/{sectionId}, body `{state}` — advances a delegated
 * section's completion marker (never a step backward, per the server's
 * own `_WRITABLE_STATES`). Called once the contributor's own Continue
 * validates, alongside (never instead of) the WorkingCopyPort write that
 * actually persists the answers. */
export async function putContributorSectionState(
  sectionId: string,
  state: Exclude<ContributorSectionState, "todo">,
): Promise<ContributorSectionApiResult<{ sectionId: string; state: string }>> {
  let response: Response;
  try {
    response = await authenticatedFetch(`/api/portal/sections/${encodeURIComponent(sectionId)}`, {
      method: "PUT",
      headers: JSON_HEADERS,
      body: JSON.stringify({ state }),
    });
  } catch {
    return { ok: false, error: { code: "unknown", message: "Something went wrong. Try again." } };
  }
  if (response.status === 200) {
    const body = (await response.json()) as { sectionId: string; state: string };
    return { ok: true, value: { sectionId: body.sectionId, state: body.state } };
  }
  return { ok: false, error: await parseError(response) };
}

export async function submitContributorSections(): Promise<ContributorSectionApiResult<{ status: string }>> {
  let response: Response;
  try {
    response = await authenticatedFetch("/api/portal/sections/submit", { method: "POST", headers: JSON_HEADERS });
  } catch {
    return { ok: false, error: { code: "unknown", message: "Something went wrong. Try again." } };
  }
  if (response.status === 200) {
    const body = (await response.json()) as { status: string };
    return { ok: true, value: { status: body.status } };
  }
  return { ok: false, error: await parseError(response) };
}
